import { triniumApi } from '@api/Trinium/slice';
import {
	Charge,
	BaseChargeRequest,
	PatchChargeRequest,
	ChargeResponseRaw,
	Payment,
	transformChargeResponse,
	transformPaymentResponse,
	DeleteChargeRequest,
	PostPaymentRequest,
	PatchPaymentRequest,
	PutPaymentResponse,
	DeleteChargeResponse,
	updateChargesAndPaymentsCache,
	GetRatePutRequest,
	AddNotePutRequest,
	transformPayment,
	ReleasePaymentApiRequest,
	DeletePaymentsApiRequest
} from './types';

export const chargesAndPaymentsSlice = triniumApi.injectEndpoints({
	endpoints: (builder) => ({
		fetchCharges: builder.query<Charge[], BaseChargeRequest>({
			query: (request) => ({
				url: `${request.terminalNumber}/charges`,
				params: {
					csnId: request.csnId
				}
			}),
			providesTags: (_result, _error, arg) => [{ type: 'charges', id: arg.csnId }],
			transformResponse: transformChargeResponse
		}),

		patchCharge: builder.mutation<ChargeResponseRaw, PatchChargeRequest>({
			query: ({ terminalNumber, charge: { id, ...payload } }) => ({
				url: `${terminalNumber}/charges/${String(id)}`,
				method: 'PATCH',
				body: { ...payload }
			}),
			async onQueryStarted(params, { queryFulfilled, dispatch }) {
				let optimisticUpdates = undefined;
				if (params.optimistic) {
					optimisticUpdates = dispatch(
						chargesAndPaymentsSlice.util.updateQueryData(
							'fetchCharges',
							{
								terminalNumber: params.terminalNumber,
								csnId: params.csnId
							},
							(draft) => {
								const charge = draft.find((x) => x.id === params.charge.id);
								if (charge) {
									Object.assign(charge, { ...params.charge });
								}
							}
						)
					);
				}

				try {
					const result = await queryFulfilled;
					await updateChargesAndPaymentsCache(params.csnId, result.data);
				} catch {
					optimisticUpdates?.undo();
				}
			}
		}),

		deleteCharge: builder.mutation<DeleteChargeResponse, DeleteChargeRequest>({
			query: ({ terminalNumber, id }) => ({
				url: `${terminalNumber}/charges/${String(id)}`,
				method: 'DELETE'
			}),
			async onQueryStarted(params, { queryFulfilled, dispatch }) {
				/** Optimistically remove the Charge from cache */
				const optimistic = dispatch(
					chargesAndPaymentsSlice.util.updateQueryData(
						'fetchCharges',
						{
							terminalNumber: params.terminalNumber,
							csnId: params.csnId
						},
						(draft) => {
							const charges = [...draft];
							charges.splice(
								charges.findIndex((x) => x.id === params.id),
								1
							);

							return charges;
						}
					)
				);

				try {
					const result = await queryFulfilled;
					await updateChargesAndPaymentsCache(params.csnId, result.data);
				} catch {
					optimistic.undo();
				}
			}
		}),

		fetchPayments: builder.query<Payment[], BaseChargeRequest>({
			query: (request) => ({
				url: `${request.terminalNumber}/payments`,
				params: {
					csnId: request.csnId
				}
			}),
			providesTags: (_result, _error, arg) => [{ type: 'payments', id: arg.csnId }],
			transformResponse: transformPaymentResponse
		}),

		postPayment: builder.mutation<ChargeResponseRaw, PostPaymentRequest>({
			query: ({ terminalNumber, csnId, payment }) => ({
				url: `${terminalNumber}/payments`,
				method: 'POST',
				body: { csnId, ...payment }
			}),
			async onQueryStarted(params, { queryFulfilled }) {
				const result = await queryFulfilled;
				await updateChargesAndPaymentsCache(params.csnId, result.data);
			}
		}),

		patchPayment: builder.mutation<ChargeResponseRaw, PatchPaymentRequest>({
			query: ({ terminalNumber, payment: { id, ...payload } }) => ({
				url: `${terminalNumber}/payments/${String(id)}`,
				method: 'PATCH',
				body: { ...payload }
			}),
			async onQueryStarted(params, { queryFulfilled, dispatch }) {
				let optimisticUpdates = undefined;
				if (params.optimistic) {
					optimisticUpdates = dispatch(
						chargesAndPaymentsSlice.util.updateQueryData(
							'fetchPayments',
							{
								terminalNumber: params.terminalNumber,
								csnId: params.csnId
							},
							(draft) => {
								const payment = draft.find((x) => x.id === params.payment.id);
								if (payment) {
									Object.assign(payment, {
										...params.payment
									});
								}
							}
						)
					);
				}

				try {
					const result = await queryFulfilled;
					await updateChargesAndPaymentsCache(params.csnId, result.data);
				} catch {
					optimisticUpdates?.undo();
				}
			}
		}),

		deletePayment: builder.mutation<DeleteChargeResponse, DeleteChargeRequest>({
			query: ({ terminalNumber, id }) => ({
				url: `${terminalNumber}/payments/${String(id)}`,
				method: 'DELETE'
			}),
			async onQueryStarted(params, { queryFulfilled, dispatch }) {
				/** Optimistically remove the Charge from cache */
				const optimistic = dispatch(
					chargesAndPaymentsSlice.util.updateQueryData(
						'fetchPayments',
						{
							terminalNumber: params.terminalNumber,
							csnId: params.csnId
						},
						(draft) => {
							const payments = [...draft];
							payments.splice(
								payments.findIndex((x) => x.id === params.id),
								1
							);

							return payments;
						}
					)
				);

				try {
					const result = await queryFulfilled;

					await updateChargesAndPaymentsCache(params.csnId, result.data);
				} catch {
					optimistic.undo();
				}
			}
		}),

		/** Deletes a set of payments in a single request; the API deletes all of them or none */
		deletePayments: builder.mutation<DeleteChargeResponse, DeletePaymentsApiRequest>({
			query: ({ terminalNumber, action, data }) => ({
				url: `${terminalNumber}/payments`,
				method: 'PUT',
				body: {
					action,
					data
				}
			}),
			async onQueryStarted(params, { queryFulfilled, dispatch }) {
				/** Optimistically remove the Payments from cache */
				const optimistic = dispatch(
					chargesAndPaymentsSlice.util.updateQueryData(
						'fetchPayments',
						{
							terminalNumber: params.terminalNumber,
							csnId: params.consignmentId
						},
						(draft) => draft.filter((payment) => !params.data.paymentIds.includes(payment.id))
					)
				);

				try {
					const result = await queryFulfilled;

					await updateChargesAndPaymentsCache(params.consignmentId, result.data);
				} catch {
					optimistic.undo();
				}
			}
		}),

		getRate: builder.query<PutPaymentResponse, GetRatePutRequest>({
			query: ({ terminalNumber, ...putRequest }) => ({
				url: `${terminalNumber}/payments`,
				method: 'PUT',
				body: {
					...putRequest
				}
			})
		}),

		addPaymentNote: builder.mutation<Payment, AddNotePutRequest>({
			query: ({ terminalNumber, action, data: { paymentId, note, type } }) => ({
				url: `${terminalNumber}/payments/${String(paymentId)}`,
				method: 'PUT',
				body: {
					action,
					data: {
						note,
						type
					}
				}
			}),
			transformResponse: transformPayment,
			async onQueryStarted(params, { queryFulfilled, dispatch }) {
				const result = await queryFulfilled;

				dispatch(
					chargesAndPaymentsSlice.util.updateQueryData(
						'fetchPayments',
						{
							terminalNumber: params.terminalNumber,
							csnId: params.data.csnId
						},
						(draft) => {
							const payment = draft.find((p) => p.id === params.data.paymentId);
							if (payment) {
								Object.assign(payment, result.data);
							}
						}
					)
				);
			}
		}),

		releasePayment: builder.mutation<Payment[], ReleasePaymentApiRequest>({
			query: ({ terminalNumber, action, data }) => ({
				url: `${terminalNumber}/payments`,
				method: 'PUT',
				body: {
					action,
					data
				}
			}),
			transformResponse: transformPaymentResponse,
			async onQueryStarted(params, { queryFulfilled, dispatch }) {
				const result = await queryFulfilled;

				dispatch(
					chargesAndPaymentsSlice.util.updateQueryData(
						'fetchPayments',
						{
							terminalNumber: params.terminalNumber,
							csnId: params.consignmentId
						},
						(draft) => {
							result.data.forEach((updatedPayment) => {
								const payment = draft.find((p) => p.id === updatedPayment.id);
								if (payment) {
									Object.assign(payment, updatedPayment);
								}
							});
						}
					)
				);
			}
		})
	})
});

export const {
	useFetchChargesQuery,
	useFetchPaymentsQuery,
	usePatchChargeMutation,
	usePatchPaymentMutation,
	usePostPaymentMutation,
	useDeletePaymentMutation,
	useDeleteChargeMutation,
	useLazyGetRateQuery,
	useAddPaymentNoteMutation
} = chargesAndPaymentsSlice;
